import {
  DEFAULT_TEXT_SCALE,
  NOTICE_TEXT_SCALE_FACTORS,
  type ResolvedNoticeAppearance,
} from "../shared/appearance";
import { FONT_SIZE_PRECISION, PX_LENGTH_RE } from "./constants";
import type { StyleMap } from "./types";

const round = (value: number): number =>
  Math.round(value * FONT_SIZE_PRECISION) / FONT_SIZE_PRECISION;

/** One font size times `factor`; lengths other than px already follow their parent. */
export const scaleFontSize = (
  size: string | number | undefined,
  factor: number,
): string | number | undefined => {
  if (factor === 1 || size === undefined) {
    return size;
  }
  if (typeof size === "number") {
    return round(size * factor);
  }
  const match = PX_LENGTH_RE.exec(size);
  return match ? `${round(Number(match[1]) * factor)}px` : size;
};

/**
 * Every px font size in a style map, times `factor`; other lengths (`em`,
 * `%`) already follow their parent. A factor of 1 returns the map unchanged.
 */
export const scaleFontSizes = <T extends StyleMap>(
  styles: T,
  factor: number,
): T => {
  if (factor === 1) {
    return styles;
  }
  return Object.fromEntries(
    Object.entries(styles).map(([key, style]) => [
      key,
      style.fontSize === undefined
        ? style
        : { ...style, fontSize: scaleFontSize(style.fontSize, factor) },
    ]),
  ) as T;
};

/** The multiplier the appearance's text scale asks for. */
export const textScaleFactor = (appearance: ResolvedNoticeAppearance): number =>
  NOTICE_TEXT_SCALE_FACTORS[appearance.textScale ?? DEFAULT_TEXT_SCALE];
